/**
 * contract-editor-page.tsx — Lập / xem hợp đồng. Route `/platform/contracts/new` và `/platform/contracts/:id`.
 *
 * Nháp: form (khách, nội dung, điều khoản — mặc định theo Mẫu hợp đồng) + "Điền sẵn từ báo giá" (chỉ đọc) + Lưu / Gửi.
 * Đã gửi trở đi: chỉ đọc + hành động (gửi lại, huỷ, xem trước / tải PDF đã ký) + nhật ký sự kiện (chứng cứ ký).
 * Link ký chỉ hiện MỘT lần ngay sau khi gửi (server chỉ lưu băm) — dùng để sao chép gửi Zalo nếu email lỗi.
 */
import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { ArrowLeft, Ban, Copy, Download, Eye, Loader2, Save, Send } from 'lucide-react'
import { ErrorState, Loading } from '@/components/shared/feedback'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/misc'
import { platformApiError } from '@/lib/platform-client'
import { cn } from '@/lib/utils'
import { useManualQuotes } from '@/hooks/use-manual-quotes'
import {
  loadQuotePrefill, openContractPdf, useCancelContract, useContract, useContractTemplate, useSaveContract, useSendContract,
} from '@/hooks/use-contracts'
import { ClauseEditor } from './clause-editor'
import { ImportTextDialog, LayoutPanel } from './contract-layout-panel'
import {
  contractToForm, countBlanks, emptyForm, EVENT_LABELS, EVENT_WARN, fmtDateTime, fmtMoney, formToInput, STATUS_LABELS, STATUS_VARIANT, timeLeft,
  otherRole, validateForm, type Contract, type ContractForm,
} from './contract-helpers'

function Section({ title, children, className }: { title: string; children: React.ReactNode; className?: string }) {
  return (
    <section className={cn('space-y-3 rounded-lg border bg-card p-4', className)}>
      <h2 className="text-xs font-semibold uppercase tracking-wide text-primary">{title}</h2>
      {children}
    </section>
  )
}

export function ContractEditorPage() {
  const { id } = useParams<{ id: string }>()
  const isNew = !id || id === 'new'
  const { data, isLoading, isError } = useContract(isNew ? undefined : id)
  if (isNew) return <Editor contract={null} />
  if (isLoading) return <Loading label="Đang tải..." />
  if (isError || !data) return <ErrorState message="Không tải được hợp đồng" />
  return data.status === 'draft' ? <Editor key={data.id} contract={data} /> : <Detail contract={data} />
}

// ── Nháp: form ───────────────────────────────────────────────────────

function Editor({ contract }: { contract: Contract | null }) {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const tpl = useContractTemplate()
  const save = useSaveContract()
  const send = useSendContract()
  const quotes = useManualQuotes({ page: 1 })
  const [form, setForm] = useState<ContractForm>(() => (contract ? contractToForm(contract) : emptyForm()))
  const [linkInfo, setLinkInfo] = useState<{ url: string; emailSent: boolean } | null>(null)
  const set = <K extends keyof ContractForm>(k: K, v: ContractForm[K]) => setForm((f) => ({ ...f, [k]: v }))

  // Điều khoản mặc định theo mẫu khi lập mới (người dùng sửa thì thành điều khoản riêng của hợp đồng này).
  const [customClauses, setCustomClauses] = useState(!!contract)
  const clauses = form.clauses ?? tpl.data?.template.clauses ?? []
  const [importing, setImporting] = useState(false)
  const [confirmBlanks, setConfirmBlanks] = useState(false)
  const blanks = customClauses ? countBlanks(clauses) : 0
  const customerRole = otherRole(form.layout.sellerRole)

  const fillFromQuote = async (quoteId: string) => {
    try {
      const p = await loadQuotePrefill(quoteId)
      setForm((f) => ({
        ...f, quoteId: p.quoteId, orgId: p.orgId, fullName: p.customer.fullName || f.fullName, address: p.customer.address || f.address,
        email: p.customer.email || f.email, phone: p.customer.phone || f.phone, description: p.deal.description, amount: p.deal.amount === null ? '' : String(p.deal.amount),
      }))
      toast.success(`Đã điền sẵn từ báo giá ${p.quoteNumber} — kiểm tra lại họ tên, CCCD`)
    } catch (err) { toast.error(platformApiError(err)) }
  }
  const quoteParam = params.get('quote')
  useEffect(() => { if (!contract && quoteParam) void fillFromQuote(quoteParam) }, [contract, quoteParam])

  const submit = (then?: 'send', skipBlankCheck = false) => {
    const err = validateForm({ ...form, clauses: customClauses ? clauses : null })
    if (err) return void toast.error(err)
    if (then === 'send' && blanks > 0 && !skipBlankCheck) return void setConfirmBlanks(true)
    const input = formToInput({ ...form, clauses: customClauses ? clauses : null })
    save.mutate({ id: contract?.id, input }, {
      onSuccess: (c) => {
        if (then !== 'send') { toast.success('Đã lưu hợp đồng nháp'); navigate(`/platform/contracts/${c.id}`, { replace: true }); return }
        send.mutate({ id: c.id }, {
          onSuccess: (r) => setLinkInfo({ url: r.signUrl, emailSent: r.emailSent }),
          onError: (e) => { toast.error(platformApiError(e)); navigate(`/platform/contracts/${c.id}`, { replace: true }) },
        })
      },
      onError: (e) => toast.error(platformApiError(e)),
    })
  }

  const busy = save.isPending || send.isPending
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button asChild variant="ghost" size="sm"><Link to="/platform/contracts"><ArrowLeft className="h-4 w-4" /> Hợp đồng</Link></Button>
        <h1 className="text-lg font-semibold">{contract ? `Sửa hợp đồng ${contract.number}` : 'Lập hợp đồng'}</h1>
        {contract && <Badge variant={STATUS_VARIANT.draft}>{STATUS_LABELS.draft}</Badge>}
      </div>

      {tpl.data && !tpl.data.template.hasSignature && (
        <p className="rounded-md bg-warning/10 px-3 py-2 text-sm text-warning">
          Mẫu hợp đồng chưa có ảnh chữ ký bên bán — vẫn lưu nháp được nhưng chưa gửi được.{' '}
          <Link className="underline" to="/platform/contracts?tab=template">Tải ảnh chữ ký</Link>
        </p>
      )}

      {!contract && (
        <Section title="Điền sẵn từ báo giá (tuỳ chọn)">
          <select
            aria-label="Chọn báo giá thủ công" defaultValue=""
            className="flex h-8 w-full max-w-md rounded-md border border-input bg-card px-2 py-1 text-sm"
            onChange={(e) => { if (e.target.value) void fillFromQuote(e.target.value) }}
          >
            <option value="">— Không điền —</option>
            {(quotes.data?.items ?? []).map((q) => <option key={q.id} value={q.id}>{q.number} · {q.customerName || 'Khách'} · {fmtMoney(q.total)}</option>)}
          </select>
          <p className="text-xs text-muted-foreground">Chỉ lấy họ tên / email / SĐT / địa chỉ, nội dung và tổng tiền để điền form — hợp đồng độc lập với báo giá.</p>
        </Section>
      )}

      <Section title={`Khách hàng (cá nhân — Bên ${customerRole})`}>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1"><Label htmlFor="c-name">Họ và tên *</Label><Input id="c-name" value={form.fullName} maxLength={120} onChange={(e) => set('fullName', e.target.value)} /></div>
          <div className="space-y-1"><Label htmlFor="c-id">Số CCCD/CMND</Label><Input id="c-id" value={form.idNumber} maxLength={20} onChange={(e) => set('idNumber', e.target.value)} /></div>
          <div className="space-y-1"><Label htmlFor="c-email">Email * (nhận link ký + mã OTP)</Label><Input id="c-email" type="email" value={form.email} maxLength={200} onChange={(e) => set('email', e.target.value)} /></div>
          <div className="space-y-1"><Label htmlFor="c-phone">Số điện thoại</Label><Input id="c-phone" value={form.phone} maxLength={20} onChange={(e) => set('phone', e.target.value)} /></div>
          <div className="space-y-1 sm:col-span-2"><Label htmlFor="c-addr">Địa chỉ</Label><Input id="c-addr" value={form.address} maxLength={300} onChange={(e) => set('address', e.target.value)} /></div>
        </div>
      </Section>

      <Section title="Nội dung dịch vụ">
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="space-y-1 sm:col-span-2"><Label htmlFor="c-desc">Dịch vụ / nội dung</Label><Input id="c-desc" value={form.description} maxLength={300} onChange={(e) => set('description', e.target.value)} /></div>
          <div className="space-y-1"><Label htmlFor="c-amt">Số tiền (đồng)</Label><Input id="c-amt" inputMode="numeric" value={form.amount} onChange={(e) => set('amount', e.target.value.replace(/\D/g, ''))} /></div>
          <div className="space-y-1 sm:col-span-3"><Label htmlFor="c-note">Ghi chú nội bộ</Label><Textarea id="c-note" className="min-h-[50px]" maxLength={500} value={form.note} onChange={(e) => set('note', e.target.value)} /></div>
        </div>
      </Section>

      <Section title="Bố cục hợp đồng">
        <LayoutPanel layout={form.layout} onChange={(l) => set('layout', l)} />
      </Section>

      <Section title="Điều khoản">
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" size="sm" variant="outline" onClick={() => setImporting(true)}>Nhập từ văn bản (dán từ Word)</Button>
          {blanks > 0 && <span className="text-xs text-warning">Còn {blanks} chỗ “......” chưa điền trong điều khoản — điền trước khi gửi cho khách.</span>}
        </div>
        {!customClauses ? (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Đang dùng {tpl.data?.template.clauses.length ?? '…'} điều khoản của Mẫu hợp đồng.</p>
            <Button type="button" size="sm" variant="outline" disabled={!tpl.data} onClick={() => { set('clauses', tpl.data!.template.clauses); setCustomClauses(true) }}>Soạn điều khoản riêng cho hợp đồng này</Button>
          </div>
        ) : (
          <ClauseEditor clauses={clauses} variables={tpl.data?.variables ?? []} onChange={(c) => set('clauses', c)} />
        )}
      </Section>

      <div className="flex flex-wrap justify-end gap-2">
        {contract && (
          <Button variant="outline" onClick={() => openContractPdf(contract.id, 'preview-pdf', '').catch((e) => toast.error(platformApiError(e)))}><Eye className="h-4 w-4" /> Xem trước PDF</Button>
        )}
        <Button variant="outline" disabled={busy} onClick={() => submit()}>{save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Lưu nháp</Button>
        <Button disabled={busy || (tpl.data ? !tpl.data.template.hasSignature : false)} onClick={() => submit('send')}>
          {send.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Lưu & gửi cho khách
        </Button>
      </div>

      {linkInfo && <LinkDialog info={linkInfo} onClose={() => { setLinkInfo(null); navigate('/platform/contracts') }} />}
      {importing && (
        <ImportTextDialog
          onClose={() => setImporting(false)}
          onImport={(r) => {
            setForm((f) => ({
              ...f, clauses: r.clauses,
              layout: { ...f.layout, partiesInText: true, ...(r.title ? { title: r.title } : {}) },
            }))
            setCustomClauses(true)
            setImporting(false)
            toast.success(`Đã nhập ${r.clauses.length} điều khoản (chế độ toàn văn) — kiểm tra rồi Lưu nháp`)
          }}
        />
      )}
      {confirmBlanks && (
        <Dialog open onOpenChange={(o) => !o && setConfirmBlanks(false)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Còn {blanks} chỗ chưa điền</DialogTitle>
              <DialogDescription>
                Điều khoản vẫn còn các chỗ “......” để trống. Khách sẽ thấy đúng như vậy khi ký. Bạn muốn gửi luôn hay quay lại điền?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmBlanks(false)}>Quay lại điền</Button>
              <Button onClick={() => { setConfirmBlanks(false); submit('send', true) }}>Vẫn gửi cho khách</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}

function LinkDialog({ info, onClose }: { info: { url: string; emailSent: boolean }; onClose: () => void }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{info.emailSent ? 'Đã gửi link ký cho khách' : 'Gửi email thất bại — hãy gửi link thủ công'}</DialogTitle>
          <DialogDescription>
            {info.emailSent ? 'Khách nhận email có link ký. ' : 'Hệ thống chưa gửi được email (kiểm tra cấu hình SMTP). '}
            Link dưới đây chỉ hiển thị MỘT lần (hệ thống chỉ lưu bản băm) — có thể sao chép gửi qua Zalo. Mã OTP luôn gửi về email khách.
          </DialogDescription>
        </DialogHeader>
        <Input readOnly value={info.url} onFocus={(e) => e.currentTarget.select()} aria-label="Link ký" />
        <DialogFooter>
          <Button variant="outline" onClick={() => navigator.clipboard.writeText(info.url).then(() => toast.success('Đã sao chép link'), () => toast.error('Không sao chép được — hãy bôi đen và chép tay'))}><Copy className="h-4 w-4" /> Sao chép</Button>
          <Button onClick={onClose}>Xong</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Đã gửi trở đi: chi tiết ──────────────────────────────────────────

function Detail({ contract: c }: { contract: Contract }) {
  const send = useSendContract()
  const cancel = useCancelContract()
  const [linkInfo, setLinkInfo] = useState<{ url: string; emailSent: boolean } | null>(null)
  const [cancelling, setCancelling] = useState(false)
  const [reason, setReason] = useState('')
  const st = c.displayStatus
  const canResend = c.status === 'sent'

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button asChild variant="ghost" size="sm"><Link to="/platform/contracts"><ArrowLeft className="h-4 w-4" /> Hợp đồng</Link></Button>
        <h1 className="font-mono text-lg font-semibold">{c.number}</h1>
        <Badge variant={STATUS_VARIANT[st]}>{STATUS_LABELS[st]}</Badge>
        {c.status === 'sent' && c.expiresAt && <span className="text-xs text-muted-foreground">hạn {fmtDateTime(c.expiresAt)} ({timeLeft(c.expiresAt)})</span>}
        <div className="ml-auto flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => openContractPdf(c.id, 'preview-pdf', '').catch((e) => toast.error(platformApiError(e)))}><Eye className="h-4 w-4" /> Xem PDF</Button>
          {c.status === 'signed' && (
            <Button size="sm" onClick={() => openContractPdf(c.id, 'pdf', `${c.number}-da-ky.pdf`).catch((e) => toast.error(platformApiError(e)))}><Download className="h-4 w-4" /> Tải PDF đã ký</Button>
          )}
          {canResend && (
            <Button
              variant="outline" size="sm" disabled={send.isPending}
              onClick={() => send.mutate({ id: c.id }, { onSuccess: (r) => setLinkInfo({ url: r.signUrl, emailSent: r.emailSent }), onError: (e) => toast.error(platformApiError(e)) })}
            >
              {send.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Gửi lại (link mới)
            </Button>
          )}
          {(c.status === 'sent') && <Button variant="outline" size="sm" onClick={() => setCancelling(true)}><Ban className="h-4 w-4 text-destructive" /> Huỷ</Button>}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Section title={`Khách hàng (Bên ${otherRole(c.layout.sellerRole)})`}>
          <dl className="grid grid-cols-[8rem_1fr] gap-y-1 text-sm">
            <dt className="text-muted-foreground">Họ tên</dt><dd className="font-medium">{c.customer.fullName}</dd>
            <dt className="text-muted-foreground">CCCD/CMND</dt><dd>{c.customer.idNumber || '—'}</dd>
            <dt className="text-muted-foreground">Email</dt><dd>{c.customer.email}</dd>
            <dt className="text-muted-foreground">Điện thoại</dt><dd>{c.customer.phone || '—'}</dd>
            <dt className="text-muted-foreground">Địa chỉ</dt><dd>{c.customer.address || '—'}</dd>
            <dt className="text-muted-foreground">Dịch vụ</dt><dd>{c.deal.description || '—'}</dd>
            <dt className="text-muted-foreground">Số tiền</dt><dd>{fmtMoney(c.deal.amount)}</dd>
          </dl>
        </Section>
        <Section title="Chứng cứ ký">
          {c.status === 'signed' ? (
            <dl className="grid grid-cols-[8rem_1fr] gap-y-1 text-sm">
              <dt className="text-muted-foreground">Người ký</dt><dd className="font-medium">{c.signerName}</dd>
              <dt className="text-muted-foreground">Thời điểm</dt><dd>{fmtDateTime(c.signedAt)}</dd>
              <dt className="text-muted-foreground">Địa chỉ IP</dt><dd className="font-mono">{c.signedIp}</dd>
              <dt className="text-muted-foreground">Trình duyệt</dt><dd className="break-all text-xs">{c.signedUserAgent}</dd>
              <dt className="text-muted-foreground">Băm nội dung</dt><dd className="break-all font-mono text-[11px]">{c.bodyHash}</dd>
              <dt className="text-muted-foreground">Băm PDF</dt><dd className="break-all font-mono text-[11px]">{c.pdfHash}</dd>
            </dl>
          ) : (
            <p className="text-sm text-muted-foreground">
              {c.status === 'declined' ? `Khách từ chối lúc ${fmtDateTime(c.declinedAt)}${c.declineReason ? ` — ${c.declineReason}` : ''}.`
                : c.status === 'cancelled' ? `Đã huỷ lúc ${fmtDateTime(c.cancelledAt)} — ${c.cancelReason ?? ''}.`
                  : 'Chưa ký.'}
            </p>
          )}
          {c.bodyHash && c.status !== 'signed' && <p className="break-all font-mono text-[11px] text-muted-foreground">Băm nội dung: {c.bodyHash}</p>}
        </Section>
      </div>

      <Section title="Nội dung hợp đồng (đã đóng băng khi gửi)">
        <div className="space-y-2 text-sm">
          {c.body.map((cl, i) => (
            <div key={i}><p className="font-semibold">{cl.title}</p><p className="whitespace-pre-line text-muted-foreground">{cl.text}</p></div>
          ))}
        </div>
      </Section>

      <Section title="Nhật ký sự kiện">
        <ul className="divide-y text-sm">
          {(c.events ?? []).map((e) => (
            <li key={e.id} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 py-1.5">
              <span className="w-36 shrink-0 text-xs text-muted-foreground">{fmtDateTime(e.at)}</span>
              <span className={cn('font-medium', EVENT_WARN.has(e.type) && 'text-warning')}>{EVENT_LABELS[e.type] ?? e.type}</span>
              {e.ip && <span className="font-mono text-xs text-muted-foreground">{e.ip}</span>}
              {e.userAgent && <span className="max-w-[28rem] truncate text-xs text-muted-foreground" title={e.userAgent}>{e.userAgent}</span>}
            </li>
          ))}
        </ul>
      </Section>

      {linkInfo && <LinkDialog info={linkInfo} onClose={() => setLinkInfo(null)} />}
      {cancelling && (
        <Dialog open onOpenChange={(o) => !o && setCancelling(false)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Huỷ hợp đồng {c.number}</DialogTitle>
              <DialogDescription>Link ký hiện tại sẽ vô hiệu — khách không ký được nữa.</DialogDescription>
            </DialogHeader>
            <div className="space-y-1">
              <Label htmlFor="ct-cancel">Lý do</Label>
              <Textarea id="ct-cancel" className="min-h-[60px]" maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCancelling(false)}>Đóng</Button>
              <Button
                variant="destructive" disabled={cancel.isPending || !reason.trim()}
                onClick={() => cancel.mutate({ id: c.id, reason: reason.trim() }, {
                  onSuccess: () => { toast.success('Đã huỷ hợp đồng'); setCancelling(false) },
                  onError: (e) => toast.error(platformApiError(e)),
                })}
              >
                Huỷ hợp đồng
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}
